import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, ListItem, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { OrderHandler, OrderStatus } from "../orderAPI";
import { AUTOMATCHING_ON, AUTOMATCHING_PAUSED } from "../constants";
import InitialsAvatar from "../../../common/components/functional/initialsAvatar/InitialsAvatar";
import AlertWarningIcon from "../../../common/icons/alertWarning.svg";
import { canExpertPlaceAndEditOrders } from "../../user/accessRightChecks";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import isEmpty from "../../../common/helpers/isEmpty";
import { changeOrderHandlers } from "../orderRowsDetailedSlice";
import AutocompleteChip from "../../../common/components/functional/customAutocomplete/AutocompleteChip";

type Props = {
  bundleId: string;
  status: OrderStatus | undefined;
  handlers: OrderHandler[];
  handlerOptions: OrderHandler[];
  isLongOrder?: boolean;
};

export const OrderHandlerSelection = ({
  bundleId,
  status,
  handlers,
  handlerOptions: options,
  isLongOrder = false,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("order");
  const user = useAppSelector((state) => state.user.user);
  const canChangeHandler = canExpertPlaceAndEditOrders(user!!);

  const [editMode, setEditMode] = useState(false);
  const [open, setOpen] = useState(false);
  const noHandler = { id: "", name: t("noHandler") };
  const handlerOptions: OrderHandler[] = [noHandler, ...options];

  const hasHandlers = !isEmpty(handlers);
  const value = isLongOrder
    ? hasHandlers
      ? handlers
      : [noHandler]
    : [handlers[0] || noHandler];

  const [localValue, setLocalValue] = useState<OrderHandler[]>(value);

  useEffect(() => {
    setLocalValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handlers]);

  const updateOrderHandlers = (value: OrderHandler[] | OrderHandler) => {
    const v = Array.isArray(value) ? value : [value];
    const filtered = v.filter((handler) => !!handler.id);
    const ids = filtered.map((handler) => handler.id!!);
    const names = filtered.map((handler) => handler.name);
    dispatch(
      changeOrderHandlers({
        bundleId,
        handlerIds: ids,
        handlerNames: names,
      }),
    );
  };

  return !editMode ? (
    <Box sx={isLongOrder ? { paddingTop: 0 } : { paddingTop: 0 }}>
      <Box display="flex" flexDirection="column" alignItems="end">
        {hasHandlers ? (
          status?.automatchingStatus !== AUTOMATCHING_ON &&
          handlers.map((handler, i) => {
            const first = i === 0;
            return (
              <Box
                key={handler.id}
                display="flex"
                alignItems="center"
                justifyContent="end"
                sx={{
                  cursor: canChangeHandler ? "pointer" : "default",
                  mb: first && handlers.length > 1 ? 0.5 : 0,
                }}
              >
                <InitialsAvatar
                  sx={{
                    outline: "2px solid #fff",
                    marginRight: "10px",
                  }}
                  name={handler.name}
                  width="24px"
                  fontSize="10px"
                />
                <Box
                  display="flex"
                  alignItems="center"
                  onClick={() => {
                    setEditMode(true);
                    setOpen(true);
                  }}
                >
                  <Typography fontSize="16px">{handler.name}</Typography>
                  <ChevronRightIcon sx={{ fontSize: 22 }} />
                </Box>
              </Box>
            );
          })
        ) : status?.automatchingStatus === AUTOMATCHING_ON ? (
          <Typography variant="secondary">{t("automatching")}</Typography>
        ) : status?.automatchingStatus === AUTOMATCHING_PAUSED ? (
          <Box
            display="flex"
            alignItems="center"
            onClick={() => {
              setEditMode(true);
              setOpen(true);
            }}
          >
            <img
              src={AlertWarningIcon}
              alt={"warningIcon"}
              style={{ marginRight: "5px" }}
            />
            <Typography>{t("automatching")}</Typography>
            <ChevronRightIcon />
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            onClick={
              canChangeHandler
                ? () => {
                    setEditMode(true);
                    setOpen(true);
                  }
                : undefined
            }
          >
            <Typography>{t("noHandler")}</Typography>
            <ChevronRightIcon />
          </Box>
        )}
      </Box>
    </Box>
  ) : (
    <Autocomplete
      disableClearable
      sx={{
        width: "250px",
        // workaround to prevent component to resize on hover/focus -> updating the MUI package should fix this
        "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
          { minWidth: "30px" },
      }}
      options={handlerOptions}
      value={localValue}
      multiple={true}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name}
      getOptionDisabled={() => localValue.length === 2}
      onChange={(_, newValue: any) => {
        setLocalValue(newValue);
        if (!isLongOrder) {
          const value = newValue[newValue.length - 1] || noHandler;
          setLocalValue([value]);
          updateOrderHandlers([value]);
        } else {
          setLocalValue(newValue);
        }
      }}
      open={open}
      onOpen={() => setOpen(true)}
      disableCloseOnSelect={isLongOrder}
      onClose={() => {
        if (isLongOrder) {
          updateOrderHandlers(localValue);
        }
        setOpen(false);
        setEditMode(false);
      }}
      renderInput={(params) => <CommonTextField {...params} autoFocus />}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id}>
          <InitialsAvatar
            name={option.name}
            width="25px"
            fontSize="10px"
            sx={{ marginRight: "10px" }}
          />
          {option.name}
        </ListItem>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...rest } = getTagProps({ index });
          return (
            <AutocompleteChip
              key={key}
              {...rest}
              label={option.name || option.id}
            />
          );
        })
      }
    />
  );
};
